import { useState } from 'react';

export default function Hero() {
  const [showBookingModal, setShowBookingModal] = useState(false);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/3aa9544f0ee19d74fb8177a0893f73be.jpg"
            alt="MarAzul Veracruz"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center w-full">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 drop-shadow-2xl">
            Renta de Lanchas y Yates en Veracruz Mar Azul
          </h1>
          <p className="text-xl md:text-2xl text-white/95 mb-8 max-w-3xl mx-auto drop-shadow-lg">
            Vive experiencias inolvidables en el mar con nuestra flota de embarcaciones de lujo. 
            Tours personalizados, pesca deportiva y eventos especiales.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <button
              onClick={() => setShowBookingModal(true)}
              className="px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white text-lg font-bold rounded-lg shadow-2xl hover:shadow-teal-500/50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-check-line mr-2"></i>
              Reserva y Paga Ya
            </button>
            <button
              onClick={scrollToContact}
              className="px-8 py-4 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white text-lg font-semibold rounded-lg border-2 border-white/50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Ver Embarcaciones
            </button>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="w-14 h-14 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-ship-line text-2xl text-white"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Flota Moderna</h3>
              <p className="text-white/80 text-sm">Embarcaciones nuevas y bien equipadas</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="w-14 h-14 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">100% Seguro</h3>
              <p className="text-white/80 text-sm">Capitanes certificados y seguros incluidos</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="w-14 h-14 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-24-hours-line text-2xl text-white"></i>
              </div>
              <h3 className="text-white font-bold text-lg mb-2">Atención 24/7</h3>
              <p className="text-white/80 text-sm">Soporte y reservaciones todo el día</p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="animate-bounce">
            <i className="ri-arrow-down-line text-4xl text-white/80"></i>
          </div>
        </div>
      </section>

      {/* Quick Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-teal-500 to-teal-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold">Reserva y Paga Ya</h3>
                <p className="text-white/90 text-sm mt-1">Completa tu reservación en minutos</p>
              </div>
              <button
                onClick={() => setShowBookingModal(false)}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <div className="p-6">
              <QuickBookingForm onClose={() => setShowBookingModal(false)} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function QuickBookingForm({ onClose }: { onClose: () => void }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    boat: '',
    date: '',
    hours: '',
    passengers: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const boats = [
    { name: 'Lancha Deportiva', price: 2500 },
    { name: 'Yate de Lujo', price: 8000 },
    { name: 'Lancha Familiar', price: 3500 },
    { name: 'Lancha de Pesca', price: 2000 },
    { name: 'Catamarán', price: 10000 },
    { name: 'Lancha Rápida', price: 3000 }
  ];

  const calculateTotal = () => {
    const boat = boats.find(b => b.name === formData.boat);
    const hours = parseInt(formData.hours) || 0;
    if (boat && hours > 0) {
      return boat.price * hours;
    }
    return 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      formBody.append('total', calculateTotal().toString());

      const response = await fetch('https://readdy.ai/api/public/form/submit/marazul-reserva-pago', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const total = calculateTotal();

  return (
    <form id="quick-booking-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="quick-name" className="block text-sm font-semibold text-gray-700 mb-2">
            Nombre Completo *
          </label>
          <input
            type="text"
            id="quick-name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="Tu nombre"
          />
        </div>

        <div>
          <label htmlFor="quick-email" className="block text-sm font-semibold text-gray-700 mb-2">
            Email *
          </label>
          <input
            type="email"
            id="quick-email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="tu@email.com"
          />
        </div>
      </div>

      <div>
        <label htmlFor="quick-phone" className="block text-sm font-semibold text-gray-700 mb-2">
          Teléfono *
        </label>
        <input
          type="tel"
          id="quick-phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
          placeholder="229 123 4567"
        />
      </div>

      <div>
        <label htmlFor="quick-boat" className="block text-sm font-semibold text-gray-700 mb-2">
          Selecciona tu Embarcación *
        </label>
        <select
          id="quick-boat"
          name="boat"
          value={formData.boat}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
        >
          <option value="">Elige una embarcación</option>
          {boats.map((boat) => (
            <option key={boat.name} value={boat.name}>
              {boat.name} - ${boat.price.toLocaleString()}/hora
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="quick-date" className="block text-sm font-semibold text-gray-700 mb-2">
            Fecha *
          </label>
          <input
            type="date"
            id="quick-date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            required
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
          />
        </div>

        <div>
          <label htmlFor="quick-hours" className="block text-sm font-semibold text-gray-700 mb-2">
            Horas *
          </label>
          <select
            id="quick-hours"
            name="hours"
            value={formData.hours}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
          >
            <option value="">Duración</option>
            <option value="2">2 horas</option>
            <option value="3">3 horas</option>
            <option value="4">4 horas</option>
            <option value="6">6 horas</option>
            <option value="8">8 horas</option>
          </select>
        </div>

        <div>
          <label htmlFor="quick-passengers" className="block text-sm font-semibold text-gray-700 mb-2">
            Pasajeros *
          </label>
          <input
            type="number"
            id="quick-passengers"
            name="passengers"
            value={formData.passengers}
            onChange={handleChange}
            required
            min="1"
            max="20"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="Núm."
          />
        </div>
      </div>

      <div>
        <label htmlFor="quick-message" className="block text-sm font-semibold text-gray-700 mb-2">
          Mensaje Adicional
        </label>
        <textarea
          id="quick-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={3}
          maxLength={500}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
          placeholder="Detalles especiales de tu reservación..."
        ></textarea>
        <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 caracteres</p>
      </div>

      {total > 0 && (
        <div className="bg-gradient-to-r from-teal-50 to-teal-100 p-6 rounded-xl border-2 border-teal-200">
          <div className="flex justify-between items-center mb-4">
            <span className="text-gray-700 font-semibold">Total a Pagar:</span>
            <span className="text-3xl font-bold text-teal-600">${total.toLocaleString()} MXN</span>
          </div>
          <div className="text-sm text-gray-600 space-y-1">
            <p><i className="ri-check-line text-teal-500 mr-1"></i> Capitán certificado incluido</p>
            <p><i className="ri-check-line text-teal-500 mr-1"></i> Equipo de seguridad completo</p>
            <p><i className="ri-check-line text-teal-500 mr-1"></i> Seguro de viaje incluido</p>
          </div>
        </div>
      )}

      {submitStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          <i className="ri-check-circle-line mr-2"></i>
          ¡Reservación enviada! Te contactaremos pronto para confirmar el pago.
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          <i className="ri-error-warning-line mr-2"></i>
          Error al enviar. Por favor intenta de nuevo.
        </div>
      )}

      <div className="space-y-3">
        <button
          type="submit"
          disabled={isSubmitting || total === 0}
          className="w-full py-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-bold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-lg"
        >
          {isSubmitting ? 'Procesando...' : 'Continuar al Pago'}
        </button>
        
        <div className="flex items-center justify-center gap-3 text-sm text-gray-500">
          <span>Métodos de pago:</span>
          <i className="ri-bank-card-line text-xl text-gray-600"></i>
          <span className="font-semibold">MercadoPago</span>
          <span>•</span>
          <span className="font-semibold">PayPal</span>
        </div>
      </div>
    </form>
  );
}
